import React, { useEffect, useState } from "react";
import { ShareNetwork, Copy, Check, X, Image as ImageIcon, XLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { LOCAL_API_URL } from "@/lib/constants";

// Generic rich-share control for governance entities (proposals, DReps,
// committee members). The "Copy link" / X buttons share a backend share URL
// (/api/share/<sharePath>) that serves crawler-visible Open Graph / Twitter-card
// meta + a generated preview image, so the link unfurls into a card on
// X / Discord / Slack and redirects humans to the live explorer page.
// `children` renders the in-app preview card body (mirrors the social image).
export const GovShareButton = ({ tid, heading, sharePath, title, tweetText, children }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const base = (LOCAL_API_URL || "").replace(/\/+$/, "");
  const shareUrl = `${base}/api/share/${sharePath}`;
  const cardUrl = `${shareUrl}/card.png`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText || title || "")}&url=${encodeURIComponent(shareUrl)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied", { description: "Paste it anywhere — it unfurls into a preview card." });
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`${tid}-share-button`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.15em] mono transition-colors border-primary/25 bg-primary/[0.04] text-muted-foreground hover:text-foreground hover:border-[#22d3ee]/50"
      >
        <ShareNetwork size={13} /> Share
      </button>

      {open ? (
        <div
          data-testid={`${tid}-share-modal`}
          role="dialog"
          aria-modal="true"
          aria-label={heading}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="relative z-10 w-full max-w-lg bg-card border border-primary/25 rounded-xl shadow-2xl shadow-primary/10 p-5 sm:p-6 space-y-5">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-[12px] uppercase tracking-[0.2em] text-muted-foreground mono">{heading}</h3>
              <button
                type="button"
                data-testid={`${tid}-share-close`}
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* In-app preview card (mirrors the generated social image) */}
            <div data-testid={`${tid}-share-card`} className="rounded-lg overflow-hidden border border-primary/20 bg-[#080c12]">
              <div className="h-1 bg-[#22d3ee]" />
              <div className="p-5 space-y-3">{children}</div>
            </div>

            {/* Share link */}
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">Share link</div>
              <div className="flex items-center gap-2">
                <input
                  data-testid={`${tid}-share-url`}
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 bg-black/40 border border-primary/20 rounded-lg px-3 py-2 text-[11px] mono text-foreground outline-none"
                />
                <button
                  type="button"
                  data-testid={`${tid}-share-copy`}
                  onClick={copy}
                  className="shrink-0 inline-flex items-center gap-1.5 bg-primary text-primary-foreground font-bold tracking-[0.15em] uppercase text-[10px] rounded-lg px-3 py-2.5 hover:opacity-90 transition-opacity"
                >
                  {copied ? <Check size={12} weight="bold" /> : <Copy size={12} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <a
                data-testid={`${tid}-share-x`}
                href={xUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 border border-primary/25 rounded-lg px-3 py-2 text-[10px] uppercase tracking-[0.15em] mono text-muted-foreground hover:text-foreground hover:border-[#22d3ee]/50 transition-colors"
              >
                <XLogo size={13} /> Share on X
              </a>
              <a
                data-testid={`${tid}-share-image`}
                href={cardUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 border border-primary/25 rounded-lg px-3 py-2 text-[10px] uppercase tracking-[0.15em] mono text-muted-foreground hover:text-foreground hover:border-[#22d3ee]/50 transition-colors"
              >
                <ImageIcon size={13} /> Preview image
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};
